import React, {useEffect, useState} from "react";
import styles, {C} from "../styles";
import {apiCreateGroup, apiUpdateGroupThreshold, apiUpdateGroup} from "../api/groups";
import {apiCreateCommunity} from "../api/communities";
import {apiGetBackofficeSurveys} from "../api/surveys";
import {apiGetDashboard, apiGetDomains, apiAddDomain, apiDeleteDomain, apiGetWaitlist, apiProcessWaitlist} from "../api/admin";
import {apiListUsers, apiBlockUser, apiSetUserRole, apiDeleteUser, apiVerifyUser, apiPurgeUser, apiCreateUser} from "../api/users";
import {apiCreateInvite} from "../api/invites";
import {buildInviteEmailText, buildInviteLink} from "../utils";
import SurveyPieChart from "../components/SurveyPieChart";
import MoodTrendChart from "../components/MoodTrendChart";
import Modal from "../components/Modal";
import AdminNewsList from "../components/AdminNewsList";
import AiSummaryPanel from "../components/AiSummaryPanel";
import AdminIaQuizPanel from "../components/AdminIaQuizPanel";
import AdminCampaigns from "../components/AdminCampaigns";
import AdminLanding from "../components/AdminLanding";
import {useToast, useConfirm} from "../components/FeedbackProvider";

function CounterCard({emoji, label, value, color}) {
    return (
        <div style={{
            backgroundColor: "#fff",
            border: `1px solid ${C.border}`,
            borderLeft: `4px solid ${color || C.primary}`,
            borderRadius: 10,
            padding: "14px 16px",
            flex: "1 1 140px",
            minWidth: 140,
        }}>
            <div style={{fontSize: 24, marginBottom: 4}}>{emoji}</div>
            <div style={{fontSize: 24, fontWeight: 700, color: color || C.primary, fontFamily: "'JetBrains Mono', monospace"}}>{value}</div>
            <div style={{fontSize: 12, color: C.textMuted, fontWeight: 600}}>{label}</div>
        </div>
    );
}

function MoodBar({emoji, label, count, total, level}) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    const colors = {1: "#E74C3C", 2: "#E67E22", 3: "#F1C40F", 4: "#2ECC71", 5: "#1E6B5E"};
    return (
        <div style={{display: "flex", alignItems: "center", gap: 10, marginBottom: 6}}>
            <span style={{fontSize: 20, width: 28, textAlign: "center"}}>{emoji}</span>
            <div style={{flex: 1}}>
                <div style={{
                    height: 22,
                    backgroundColor: "#F0F2F0",
                    borderRadius: 6,
                    overflow: "hidden",
                    position: "relative",
                }}>
                    <div style={{
                        height: "100%",
                        width: `${pct}%`,
                        backgroundColor: colors[level] || C.primary,
                        borderRadius: 6,
                        transition: "width 0.4s ease",
                    }}/>
                    {pct > 0 && (
                        <span style={{
                            position: "absolute", top: 2, left: 8,
                            fontSize: 11, fontWeight: 700, color: pct > 15 ? "#fff" : C.text,
                        }}>{label}</span>
                    )}
                </div>
            </div>
            <span style={{fontSize: 13, fontWeight: 600, color: C.textMuted, minWidth: 42, textAlign: "right"}}>
                {count} ({pct}%)
            </span>
        </div>
    );
}


function DashboardTab({dashboard, loading}) {
    if (loading) return <p style={{color: C.textMuted, textAlign: "center", padding: 40}}>Chargement du dashboard...</p>;
    if (!dashboard) return <p style={{color: C.danger}}>Impossible de charger le dashboard.</p>;

    const {counters, mood_today, mood_history_by_group, top_ideas, active_threads, groups, entity_stats} = dashboard;

    return (
        <div>
            {/* Compteurs */}
            <div style={{display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20}}>
                <CounterCard emoji="👥" label="Agents inscrits" value={counters.users} color={C.primary}/>
                <CounterCard emoji="🏢" label="Collectifs" value={counters.groups} color="#3498DB"/>
                <CounterCard emoji="💡" label="Idées publiées" value={counters.posts} color={C.accent}/>
                <CounterCard emoji="💬" label="Messages" value={counters.community_posts} color="#9B59B6"/>
                <CounterCard emoji="🚩" label="Signalements" value={counters.reports_pending} color={counters.reports_pending > 0 ? C.danger : C.textMuted}/>
            </div>

            {/* Agents par entité */}
            {entity_stats && entity_stats.length > 0 && (
                <div style={{
                    backgroundColor: "#fff",
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: 18,
                    marginBottom: 20,
                }}>
                    <h3 style={{margin: "0 0 12px", fontSize: 15, fontFamily: "'Fraunces', serif"}}>🏛️ Agents inscrits par entité</h3>
                    {entity_stats.map(e => (
                        <div key={e.domain} style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "8px 0", borderBottom: `1px solid ${C.border}`,
                        }}>
                            <div>
                                <span style={{fontWeight: 600, fontSize: 14}}>{e.entity_name}</span>
                                <span style={{fontSize: 12, color: C.textMuted, marginLeft: 8}}>@{e.domain}</span>
                            </div>
                            <span style={{
                                backgroundColor: C.primaryLight, color: C.primary,
                                padding: "2px 10px", borderRadius: 20,
                                fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                            }}>{e.agent_count} agent{e.agent_count > 1 ? "s" : ""}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Bien-être du jour */}
            <div style={{
                backgroundColor: "#fff",
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 18,
                marginBottom: 20,
            }}>
                <h3 style={{margin: "0 0 4px", fontSize: 15, fontFamily: "'Fraunces', serif"}}>🌡️ Bien-être du jour — {mood_today.date}</h3>
                <p style={{margin: "0 0 12px", fontSize: 12, color: C.textMuted}}>
                    {mood_today.total} vote{mood_today.total > 1 ? "s" : ""} aujourd'hui (tous collectifs)
                </p>
                {mood_today.total === 0 ? (
                    <p style={{color: C.textMuted, fontStyle: "italic", fontSize: 13}}>Aucun vote aujourd'hui.</p>
                ) : (
                    mood_today.moods.map(m => (
                        <MoodBar
                            key={m.level}
                            emoji={m.emoji}
                            label={m.label}
                            count={mood_today.results[m.level]}
                            total={mood_today.total}
                            level={m.level}
                        />
                    ))
                )}
            </div>

            {/* Tendance 7 jours par collectif */}
            {mood_history_by_group && Object.keys(mood_history_by_group).length > 0 && (
                <div style={{
                    backgroundColor: "#fff",
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: 18,
                    marginBottom: 20,
                }}>
                    <h3 style={{margin: "0 0 12px", fontSize: 15, fontFamily: "'Fraunces', serif"}}>📈 Tendance sur 7 jours — par collectif</h3>
                    <MoodTrendChart historyByGroup={mood_history_by_group} groups={groups}/>
                </div>
            )}

            {/* Collectifs */}
            <div style={{
                backgroundColor: "#fff",
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 18,
                marginBottom: 20,
            }}>
                <h3 style={{margin: "0 0 12px", fontSize: 15, fontFamily: "'Fraunces', serif"}}>🏢 Collectifs</h3>
                {groups.map(g => (
                    <div key={g.id} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "8px 0", borderBottom: `1px solid ${C.border}`,
                    }}>
                        <span style={{fontWeight: 600, fontSize: 14}}>{g.name}</span>
                        <span style={{
                            backgroundColor: C.primaryLight, color: C.primary,
                            padding: "2px 10px", borderRadius: 20,
                            fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                        }}>{g.member_count} membre{g.member_count > 1 ? "s" : ""}</span>
                    </div>
                ))}
            </div>

            <div style={{display: "flex", gap: 14, flexWrap: "wrap"}}>
                {/* Dernières idées */}
                <div style={{
                    flex: "1 1 300px",
                    backgroundColor: "#fff",
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: 18,
                }}>
                    <h3 style={{margin: "0 0 12px", fontSize: 15, fontFamily: "'Fraunces', serif"}}>💡 Dernières propositions</h3>
                    {top_ideas.length === 0 ? (
                        <p style={{color: C.textMuted, fontStyle: "italic", fontSize: 13}}>Aucune idée publiée.</p>
                    ) : (
                        top_ideas.map(idea => (
                            <div key={idea.id} style={{
                                padding: "10px 0",
                                borderBottom: `1px solid ${C.border}`,
                            }}>
                                <p style={{margin: 0, fontSize: 14, lineHeight: "20px"}}>
                                    {idea.content.length > 120 ? idea.content.slice(0, 120) + "…" : idea.content}
                                </p>
                                <div style={{display: "flex", gap: 10, marginTop: 4, fontSize: 12, color: C.textMuted}}>
                                    <span>❤️ {idea.likes} soutien{idea.likes > 1 ? "s" : ""}</span>
                                    <span>— {idea.is_anonymous ? "Anonyme" : idea.author}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Discussions actives */}
                <div style={{
                    flex: "1 1 300px",
                    backgroundColor: "#fff",
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: 18,
                }}>
                    <h3 style={{margin: "0 0 12px", fontSize: 15, fontFamily: "'Fraunces', serif"}}>💬 Discussions les plus actives</h3>
                    {active_threads.length === 0 ? (
                        <p style={{color: C.textMuted, fontStyle: "italic", fontSize: 13}}>Aucune discussion.</p>
                    ) : (
                        active_threads.map(t => (
                            <div key={t.id} style={{
                                padding: "8px 0",
                                borderBottom: `1px solid ${C.border}`,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}>
                                <div>
                                    <div style={{fontWeight: 600, fontSize: 14}}>{t.title}</div>
                                    <div style={{fontSize: 12, color: C.textMuted}}>{t.group_name}</div>
                                </div>
                                <span style={{
                                    backgroundColor: "#EDE9FE", color: "#7C3AED",
                                    padding: "2px 10px", borderRadius: 20,
                                    fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                                }}>{t.message_count} msg</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export function VisibilityPicker({groups, value, onChange}) {
    const allSelected = value.length === 0;
    function toggle(id) {
        if (value.includes(id)) onChange(value.filter(g => g !== id));
        else onChange([...value, id]);
    }
    return (
        <div style={{marginBottom: 12}}>
            <p style={{fontSize: 13, fontWeight: 600, margin: "0 0 6px", color: C.text}}>Visibilité</p>
            <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                <button
                    type="button"
                    onClick={() => onChange([])}
                    style={{
                        padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: `1.5px solid ${allSelected ? C.primary : C.border}`,
                        backgroundColor: allSelected ? C.primaryLight : "transparent",
                        color: allSelected ? C.primary : C.textMuted,
                    }}
                >Tous les collectifs</button>
                {groups.map(g => {
                    const active = value.includes(g.id);
                    return (
                        <button key={g.id} type="button" onClick={() => toggle(g.id)} style={{
                            padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                            border: `1.5px solid ${active ? C.primary : C.border}`,
                            backgroundColor: active ? C.primaryLight : "transparent",
                            color: active ? C.primary : C.textMuted,
                        }}>{g.name}</button>
                    );
                })}
            </div>
            {value.length > 0 && (
                <p style={{fontSize: 11, color: C.textMuted, margin: "4px 0 0"}}>
                    Visible uniquement pour : {groups.filter(g => value.includes(g.id)).map(g => g.name).join(", ")}
                </p>
            )}
        </div>
    );
}

export default function AdminPage({user, selectedGroup, groups, onRefresh, answers}) {
    const {notify} = useToast();
    const confirm = useConfirm();
    const isFullAdmin = user.role === "admin";
    const [newGroupName, setNewGroupName] = useState("");
    const [newGroupDescription, setNewGroupDescription] = useState("");
    const [newGroupDomain, setNewGroupDomain] = useState("");
    const [newGroupContact, setNewGroupContact] = useState("");
    const [editGroupId, setEditGroupId] = useState("");
    const [editGroupName, setEditGroupName] = useState("");
    const [editGroupDescription, setEditGroupDescription] = useState("");
    const [editGroupDomain, setEditGroupDomain] = useState("");
    const [editGroupContact, setEditGroupContact] = useState("");
    const [editGroupMsg, setEditGroupMsg] = useState("");
    const [communityTitle, setCommunityTitle] = useState("");
    const [communityDescription, setCommunityDescription] = useState("");
    const [communityVisibility, setCommunityVisibility] = useState([]);
    const [inviteLink, setInviteLink] = useState("");
    const [backofficeSurveys, setBackofficeSurveys] = useState([]);
    const [adminTab, setAdminTab] = useState("dashboard");
    const [thresholdInput, setThresholdInput] = useState("");
    const [thresholdMsg, setThresholdMsg] = useState("");
    const [dashboard, setDashboard] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [domains, setDomains] = useState([]);
    const [newDomain, setNewDomain] = useState("");
    const [domainMsg, setDomainMsg] = useState("");
    const [waitlist, setWaitlist] = useState([]);
    const [waitlistMsg, setWaitlistMsg] = useState("");
    const [processingWaitlist, setProcessingWaitlist] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [usersSort, setUsersSort] = useState("date");
    const [userActionMsg, setUserActionMsg] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [userDateFrom, setUserDateFrom] = useState("");
    const [userDateTo, setUserDateTo] = useState("");
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [createUserEmail, setCreateUserEmail] = useState("");
    const [createUserPassword, setCreateUserPassword] = useState("");
    const [createUserRole, setCreateUserRole] = useState("agent");
    const [createUserMsg, setCreateUserMsg] = useState("");

    useEffect(() => {
        if (adminTab === "domaines") {
            apiGetDomains().then(data => setDomains(Array.isArray(data) ? data : []));
            setDomainMsg("");
        }
    }, [adminTab]);

    useEffect(() => {
        if (adminTab === "liste-attente") {
            apiGetWaitlist().then(data => setWaitlist(Array.isArray(data) ? data : []));
            setWaitlistMsg("");
        }
    }, [adminTab]);

    async function handleProcessWaitlist() {
        setProcessingWaitlist(true);
        const data = await apiProcessWaitlist();
        if (data.error) {
            setWaitlistMsg("Erreur : " + data.error);
        } else {
            setWaitlistMsg(`✅ ${data.processed} invitation${data.processed > 1 ? "s" : ""} envoyée${data.processed > 1 ? "s" : ""}.`);
            apiGetWaitlist().then(d => setWaitlist(Array.isArray(d) ? d : []));
        }
        setProcessingWaitlist(false);
    }

    async function handleAddDomain() {
        setDomainMsg("");
        if (!newDomain.trim()) return;
        const data = await apiAddDomain(newDomain.trim());
        if (data.error) { setDomainMsg("Erreur : " + data.error); return; }
        setNewDomain("");
        setDomainMsg("✅ Domaine ajouté.");
        apiGetDomains().then(d => setDomains(Array.isArray(d) ? d : []));
    }

    async function handleDeleteDomain(id) {
        if (!(await confirm("Supprimer ce domaine ? Les agents déjà inscrits ne sont pas affectés.", {danger: true}))) return;
        await apiDeleteDomain(id);
        apiGetDomains().then(d => setDomains(Array.isArray(d) ? d : []));
    }

    function loadUsers(sort) {
        apiListUsers(sort).then(data => setUsersList(Array.isArray(data) ? data : []));
    }

    useEffect(() => {
        if (adminTab === "utilisateurs") {
            loadUsers(usersSort);
            setUserActionMsg("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminTab, usersSort]);

    async function handleToggleBlock(target) {
        const action = target.blocked_at ? "réactiver" : "suspendre";
        if (!(await confirm(`Confirmer : ${action} le compte de "${target.username}" ?`))) return;
        const data = await apiBlockUser(target.id, !target.blocked_at);
        if (data.error) { setUserActionMsg("Erreur : " + data.error); return; }
        setUserActionMsg("✅ " + data.message);
        loadUsers(usersSort);
    }

    async function handleChangeRole(target, role) {
        if (!(await confirm(`Confirmer : passer "${target.username}" de "${target.role}" à "${role}" ?`))) {
            setUsersList(list => [...list]);
            return;
        }
        const data = await apiSetUserRole(target.id, role);
        if (data.error) {
            setUserActionMsg("Erreur : " + data.error);
            setUsersList(list => [...list]);
            return;
        }
        setUserActionMsg("✅ " + data.message);
        loadUsers(usersSort);
    }

    async function handleVerifyUser(target) {
        const data = await apiVerifyUser(target.id);
        if (data.error) { setUserActionMsg("Erreur : " + data.error); return; }
        setUserActionMsg("✅ " + data.message);
        loadUsers(usersSort);
    }

    async function handlePurgeUser(target) {
        if (!(await confirm(`Supprimer DÉFINITIVEMENT le compte "${target.username}" ? Cette action efface aussi le contenu publié par cet agent (idées, messages, commentaires). Irréversible.`, {danger: true}))) return;
        const data = await apiPurgeUser(target.id);
        if (data.error) { setUserActionMsg("Erreur : " + data.error); return; }
        setUserActionMsg("✅ " + data.message);
        loadUsers(usersSort);
    }

    async function handleCreateUser() {
        setCreateUserMsg("");
        const data = await apiCreateUser({email: createUserEmail, password: createUserPassword, role: createUserRole});
        if (data.error) { setCreateUserMsg("Erreur : " + data.error); return; }
        setCreateUserMsg(`✅ Compte créé — pseudo généré : ${data.user.username}`);
        setCreateUserEmail("");
        setCreateUserPassword("");
        setCreateUserRole("agent");
        loadUsers(usersSort);
    }

    async function handleDeleteUser(target) {
        if (!(await confirm(`Supprimer définitivement le compte "${target.username}" ? Cette action est irréversible ; ses publications restent visibles, anonymisées.`, {danger: true}))) return;
        const data = await apiDeleteUser(target.id);
        if (data.error) { setUserActionMsg("Erreur : " + data.error); return; }
        setUserActionMsg("✅ " + data.message);
        loadUsers(usersSort);
    }

    useEffect(() => {
        if (selectedGroup && adminTab === "sondages") {
            setThresholdInput(String(selectedGroup.min_members_threshold ?? 15));
            setThresholdMsg("");
            apiGetBackofficeSurveys(selectedGroup.id).then(data => {
                setBackofficeSurveys(Array.isArray(data) ? data : []);
            });
        }
    }, [selectedGroup, adminTab]);

    useEffect(() => {
        if (adminTab === "dashboard") {
            setDashboardLoading(true);
            apiGetDashboard().then(data => {
                if (!data.error) setDashboard(data);
                setDashboardLoading(false);
            });
        }
    }, [adminTab]);

    async function updateThreshold() {
        const value = parseInt(thresholdInput, 10);
        if (isNaN(value) || value < 1) { setThresholdMsg("Entrez un nombre entier supérieur à 0."); return; }
        const data = await apiUpdateGroupThreshold(selectedGroup.id, value);
        if (data.error) { setThresholdMsg(data.error); return; }
        setThresholdMsg(`✅ Seuil mis à jour : ${value} agent(s) minimum.`);
        onRefresh();
    }

    function createGroup() {
        apiCreateGroup(newGroupName, newGroupDescription, newGroupDomain, newGroupContact)
            .then(data => {
                if (data.error) { notify(data.error, "error"); return; }
                setNewGroupName("");
                setNewGroupDescription("");
                setNewGroupDomain("");
                setNewGroupContact("");
                onRefresh();
            });
    }

    // Le serveur refuse tout changement de domaine une fois fixé (PATCH /groups/:id) :
    // seul un collectif créé sans domaine peut en recevoir un.
    const editGroupDomainLocked = Boolean((groups || []).find(gr => String(gr.id) === String(editGroupId))?.domain);

    function updateGroup() {
        setEditGroupMsg("");
        apiUpdateGroup(editGroupId, {
            name: editGroupName,
            description: editGroupDescription,
            domain: editGroupDomain,
            contact_email: editGroupContact,
        }).then(data => {
            if (data.error) { setEditGroupMsg("Erreur : " + data.error); return; }
            setEditGroupMsg("✅ Collectif mis à jour.");
            onRefresh();
        });
    }

    function createCommunity() {
        if (!selectedGroup || communityTitle.trim() === "" || communityDescription.trim() === "") return;
        apiCreateCommunity(selectedGroup.id, communityTitle, communityDescription, user.username, communityVisibility)
            .then(() => {
                setCommunityTitle("");
                setCommunityDescription("");
                setCommunityVisibility([]);
                onRefresh();
            });
    }

    async function generateInvite() {
        if (!selectedGroup) { notify("Sélectionnez d'abord un collectif.", "error"); return; }
        const data = await apiCreateInvite(selectedGroup.id);
        if (data.error) { notify(data.error, "error"); return; }

        const link = buildInviteLink(data.token);
        setInviteLink(link);

        const {body} = buildInviteEmailText(data.group_name, link);
        navigator.clipboard.writeText(body);
        notify("Invitation générée et copiée dans le presse-papiers !", "success");
    }

    function tabStyle(t) {
        const active = adminTab === t;
        return {
            padding: "7px 14px",
            borderRadius: 8,
            border: active ? "none" : `1px solid #DEDED8`,
            backgroundColor: active ? "#1E6B5E" : "transparent",
            color: active ? "white" : "#4B5563",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 13,
            marginRight: 6,
        };
    }

    const filteredUsers = usersList.filter(u => {
        const q = userSearch.trim().toLowerCase();
        const matchesSearch = !q || u.username.toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
        const day = u.created_at ? u.created_at.slice(0, 10) : "";
        const matchesFrom = !userDateFrom || (day && day >= userDateFrom);
        const matchesTo = !userDateTo || (day && day <= userDateTo);
        return matchesSearch && matchesFrom && matchesTo;
    });

    return (
        <div style={styles.adminBox}>
            <h2 style={{fontFamily: "'Fraunces', serif"}}>🛠 Administration SAPPM</h2>

            <div style={{marginBottom: 16}}>
                <button onClick={() => setAdminTab("dashboard")} style={tabStyle("dashboard")}>📈 Dashboard</button>
                <button onClick={() => setAdminTab("actualites")} style={tabStyle("actualites")}>📰 Actualités</button>
                {isFullAdmin && <button onClick={() => setAdminTab("domaines")} style={tabStyle("domaines")}>🔑 Domaines</button>}
                {isFullAdmin && <button onClick={() => setAdminTab("liste-attente")} style={tabStyle("liste-attente")}>⏳ Liste d'attente</button>}
                {isFullAdmin && <button onClick={() => setAdminTab("gestion")} style={tabStyle("gestion")}>Gestion</button>}
                {isFullAdmin && <button onClick={() => setAdminTab("sondages")} style={tabStyle("sondages")}>📊 Résultats sondages</button>}
                {isFullAdmin && <button onClick={() => setAdminTab("utilisateurs")} style={tabStyle("utilisateurs")}>👥 Utilisateurs</button>}
                {isFullAdmin && <button onClick={() => setAdminTab("ia-quiz")} style={tabStyle("ia-quiz")}>🤖 Banque QCM IA</button>}
                {isFullAdmin && <button onClick={() => setAdminTab("campagnes")} style={tabStyle("campagnes")}>📣 Campagnes</button>}
                {isFullAdmin && <button onClick={() => setAdminTab("landing")} style={tabStyle("landing")}>🏠 Landing</button>}
            </div>

            {adminTab === "dashboard" && (
                <>
                    <DashboardTab dashboard={dashboard} loading={dashboardLoading} />
                    {isFullAdmin && selectedGroup && (
                        <AiSummaryPanel groupId={selectedGroup.id} groupName={selectedGroup.name} />
                    )}
                </>
            )}

            {adminTab === "actualites" && (
                <AdminNewsList user={user}/>
            )}

            {adminTab === "domaines" && (
                <>
                    <h3 style={{fontFamily: "'Fraunces', serif"}}>🔑 Domaines autorisés à l'inscription</h3>
                    <p style={{fontSize: 13, color: C.textMuted, marginBottom: 16, lineHeight: "18px"}}>
                        Seuls les agents dont l'adresse email appartient à un de ces domaines peuvent créer un compte.
                        Les agents déjà inscrits ne sont pas affectés par une suppression.
                    </p>

                    {/* Liste des domaines */}
                    <div style={{
                        backgroundColor: "#fff",
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        marginBottom: 20,
                        overflow: "hidden",
                    }}>
                        {domains.length === 0 && (
                            <p style={{color: C.textMuted, padding: 16, fontStyle: "italic", fontSize: 13}}>Aucun domaine autorisé.</p>
                        )}
                        {domains.map(d => (
                            <div key={d.id} style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
                            }}>
                                <span style={{fontWeight: 600, fontSize: 14}}>@{d.domain}</span>
                                <button onClick={() => handleDeleteDomain(d.id)} style={{
                                    ...styles.dangerButton, fontSize: 12, padding: "4px 10px",
                                }}>Supprimer</button>
                            </div>
                        ))}
                    </div>

                    {/* Ajout */}
                    <h4 style={{margin: "0 0 8px", fontFamily: "'Fraunces', serif"}}>Ajouter un domaine</h4>
                    <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap"}}>
                        <input
                            value={newDomain}
                            onChange={e => { setNewDomain(e.target.value); setDomainMsg(""); }}
                            placeholder="ex : service.gouv.fr"
                            style={{...styles.input, marginBottom: 0, flex: 1, minWidth: 200}}
                            onKeyDown={e => e.key === "Enter" && handleAddDomain()}
                        />
                        <button onClick={handleAddDomain} style={styles.button}>Ajouter</button>
                    </div>
                    {domainMsg && (
                        <p style={{
                            marginTop: 8, fontSize: 13,
                            color: domainMsg.startsWith("✅") ? C.primary : C.danger,
                        }}>{domainMsg}</p>
                    )}
                </>
            )}

            {adminTab === "liste-attente" && (
                <>
                    <h3 style={{fontFamily: "'Fraunces', serif"}}>⏳ Liste d'attente pseudos</h3>
                    <p style={{fontSize: 13, color: C.textMuted, marginBottom: 16, lineHeight: "18px"}}>
                        Ces demandes attendent qu'un pseudo soit disponible. Ajoutez de nouveaux
                        pseudos au code puis cliquez sur "Traiter la liste d'attente" pour envoyer
                        un lien de finalisation à chacune.
                    </p>

                    <div style={{
                        backgroundColor: "#fff",
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        marginBottom: 20,
                        overflow: "hidden",
                    }}>
                        {waitlist.length === 0 && (
                            <p style={{color: C.textMuted, padding: 16, fontStyle: "italic", fontSize: 13}}>Aucune demande en attente.</p>
                        )}
                        {waitlist.map(w => (
                            <div key={w.id} style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
                            }}>
                                <span style={{fontWeight: 600, fontSize: 14}}>{w.email}</span>
                                <span style={{fontSize: 12, color: C.textMuted}}>{w.status}</span>
                            </div>
                        ))}
                    </div>

                    <button onClick={handleProcessWaitlist} disabled={processingWaitlist} style={styles.button}>
                        {processingWaitlist ? "Traitement..." : "Traiter la liste d'attente"}
                    </button>
                    {waitlistMsg && (
                        <p style={{marginTop: 8, fontSize: 13, color: waitlistMsg.startsWith("✅") ? C.primary : C.danger}}>{waitlistMsg}</p>
                    )}
                </>
            )}

            {adminTab === "gestion" && (
                <>
                    <h3 style={{fontFamily: "'Fraunces', serif"}}>Créer un collectif</h3>
                    <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Nom du collectif" style={styles.input}/>
                    <input value={newGroupDescription} onChange={e => setNewGroupDescription(e.target.value)} placeholder="Description" style={styles.input}/>
                    <input value={newGroupDomain} onChange={e => setNewGroupDomain(e.target.value)} placeholder="Domaine autorisé, ex : pm.gouv.fr" style={styles.input}/>
                    <input value={newGroupContact} onChange={e => setNewGroupContact(e.target.value)} placeholder="Email de contact syndical" style={styles.input}/>
                    <button onClick={createGroup} style={styles.button}>Créer le collectif</button>

                    <hr/>

                    <h3 style={{fontFamily: "'Fraunces', serif"}}>✏️ Modifier un collectif existant</h3>
                    <select
                        value={editGroupId}
                        onChange={e => {
                            const id = e.target.value;
                            setEditGroupId(id);
                            const g = (groups || []).find(gr => String(gr.id) === id);
                            setEditGroupName(g ? g.name : "");
                            setEditGroupDescription(g ? g.description : "");
                            setEditGroupDomain(g ? g.domain : "");
                            setEditGroupContact(g ? g.contact_email : "");
                            setEditGroupMsg("");
                        }}
                        style={styles.input}
                    >
                        <option value="">— Choisir un collectif —</option>
                        {(groups || []).map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                    </select>
                    {editGroupId && (
                        <>
                            <input value={editGroupName} onChange={e => setEditGroupName(e.target.value)} placeholder="Nom du collectif" style={styles.input}/>
                            <input value={editGroupDescription} onChange={e => setEditGroupDescription(e.target.value)} placeholder="Description" style={styles.input}/>
                            <input value={editGroupDomain || ""} onChange={e => setEditGroupDomain(e.target.value)} placeholder="Domaine autorisé, ex : pm.gouv.fr" disabled={editGroupDomainLocked} style={{...styles.input, ...(editGroupDomainLocked ? {opacity: 0.6, cursor: "not-allowed"} : {})}}/>
                            {editGroupDomainLocked && (
                                <p style={{margin: "-4px 0 8px", fontSize: 12, color: C.textMuted}}>
                                    Le domaine d'un collectif ne peut plus être modifié après sa création.
                                </p>
                            )}
                            <input value={editGroupContact} onChange={e => setEditGroupContact(e.target.value)} placeholder="Email de contact syndical" style={styles.input}/>
                            <button onClick={updateGroup} style={styles.button}>Enregistrer les modifications</button>
                            {editGroupMsg && (
                                <p style={{marginTop: 8, fontSize: 13, color: editGroupMsg.startsWith("✅") ? C.primary : C.danger}}>
                                    {editGroupMsg}
                                </p>
                            )}
                        </>
                    )}

                    <hr/>

                    <h3 style={{fontFamily: "'Fraunces', serif"}}>💬 Créer un fil de discussion</h3>
                    <input value={communityTitle} onChange={e => setCommunityTitle(e.target.value)} placeholder="Titre du fil" style={styles.input}/>
                    <textarea value={communityDescription} onChange={e => setCommunityDescription(e.target.value)} placeholder="Description du fil" style={{...styles.input, height: 90}}/>
                    <VisibilityPicker groups={groups || []} value={communityVisibility} onChange={setCommunityVisibility}/>
                    <button onClick={createCommunity} style={styles.button}>Créer le fil</button>

                    <h3 style={{fontFamily: "'Fraunces', serif"}}>✉️ Inviter des collègues</h3>
                    <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 10px"}}>
                        Génère un lien d'invitation pour le collectif <strong>{selectedGroup?.name || "—"}</strong>.
                        Le lien est valable 30 jours.
                    </p>
                    <button onClick={generateInvite} style={styles.button}>Générer un lien d'invitation</button>
                    {inviteLink && (
                        <div style={{
                            marginTop: 10, padding: 12, backgroundColor: "#fff",
                            border: `1px solid ${C.border}`, borderRadius: 8,
                        }}>
                            <p style={{fontSize: 12, color: C.textMuted, margin: "0 0 6px"}}>Lien copié ! Vous pouvez aussi le partager directement :</p>
                            <input
                                readOnly value={inviteLink}
                                onClick={e => { e.target.select(); navigator.clipboard.writeText(inviteLink); }}
                                style={{...styles.input, marginBottom: 0, fontSize: 12, cursor: "pointer", backgroundColor: "#F9FAFB"}}
                            />
                        </div>
                    )}
                </>
            )}

            {adminTab === "sondages" && (
                <>
                    <h3 style={{fontFamily: "'Fraunces', serif"}}>📊 Résultats des sondages — {selectedGroup?.name || "aucun collectif sélectionné"}</h3>

                    {/* Bloc réglage du seuil */}
                    {selectedGroup && (
                        <div style={{
                            backgroundColor: "#fff",
                            border: "1px solid #DEDED8",
                            borderLeft: "4px solid #1E6B5E",
                            borderRadius: 8,
                            padding: "14px 16px",
                            marginBottom: 20,
                        }}>
                            <p style={{margin: "0 0 8px", fontWeight: 600, fontSize: 14}}>
                                🔒 Seuil d'affichage de l'effectif de ce collectif
                            </p>
                            <p style={{margin: "0 0 12px", fontSize: 13, color: "#6B7280", lineHeight: "18px"}}>
                                Le nombre d'inscrits de ce collectif n'est affiché aux agents que s'il atteint ce seuil. Ce réglage ne concerne
                                pas les sondages : leurs résultats suivent un seuil unique, fixé par la plateforme (public concerné et nombre
                                de participations minimaux). Actuellement : <strong>{selectedGroup.min_members_threshold ?? 15} agent(s)</strong>.
                            </p>
                            <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap"}}>
                                <input
                                    type="number"
                                    min="1"
                                    max="500"
                                    value={thresholdInput}
                                    onChange={e => { setThresholdInput(e.target.value); setThresholdMsg(""); }}
                                    style={{...styles.input, width: 100, marginBottom: 0}}
                                    placeholder="ex : 15"
                                />
                                <button onClick={updateThreshold} style={styles.button}>
                                    Enregistrer le seuil
                                </button>
                            </div>
                            {thresholdMsg && (
                                <p style={{
                                    margin: "8px 0 0",
                                    fontSize: 13,
                                    color: thresholdMsg.startsWith("✅") ? "#1E6B5E" : "#C0392B"
                                }}>{thresholdMsg}</p>
                            )}
                        </div>
                    )}

                    <p style={{fontSize: 13, color: "#6B7280", marginBottom: 16}}>
                        Résultats complets (sans seuil). Réservé à l'administrateur.
                    </p>

                    {!selectedGroup && (
                        <p style={{color: "#C0392B"}}>Sélectionnez un collectif dans la barre latérale.</p>
                    )}

                    {selectedGroup && backofficeSurveys.length === 0 && (
                        <p style={{color: "#6B7280"}}>Aucun sondage pour ce collectif.</p>
                    )}

                    {backofficeSurveys.map(survey => (
                        <SurveyPieChart key={survey.id} survey={survey} answers={answers}/>
                    ))}
                </>
            )}

            {adminTab === "utilisateurs" && isFullAdmin && (
                <>
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4}}>
                        <h3 style={{fontFamily: "'Fraunces', serif", margin: 0}}>👥 Gestion des utilisateurs</h3>
                        <button onClick={() => { setShowCreateUserModal(true); setCreateUserMsg(""); }} style={styles.primaryButton}>
                            + Créer un utilisateur
                        </button>
                    </div>

                    <div style={{display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap"}}>
                        <button
                            onClick={() => setUsersSort("date")}
                            style={{
                                padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                                border: `1.5px solid ${usersSort === "date" ? C.primary : C.border}`,
                                backgroundColor: usersSort === "date" ? C.primaryLight : "transparent",
                                color: usersSort === "date" ? C.primary : C.textMuted,
                            }}
                        >Par ancienneté</button>
                        <button
                            onClick={() => setUsersSort("pseudo")}
                            style={{
                                padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                                border: `1.5px solid ${usersSort === "pseudo" ? C.primary : C.border}`,
                                backgroundColor: usersSort === "pseudo" ? C.primaryLight : "transparent",
                                color: usersSort === "pseudo" ? C.primary : C.textMuted,
                            }}
                        >Par pseudo (A→Z)</button>
                    </div>

                    <div style={{display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center"}}>
                        <input
                            value={userSearch}
                            onChange={e => setUserSearch(e.target.value)}
                            placeholder="Rechercher un pseudo ou un email..."
                            style={{...styles.input, marginBottom: 0, flex: "1 1 220px"}}
                        />
                        <label style={{fontSize: 12, color: C.textMuted, display: "flex", alignItems: "center", gap: 4}}>
                            Du
                            <input type="date" value={userDateFrom} onChange={e => setUserDateFrom(e.target.value)}
                                   style={{...styles.input, marginBottom: 0, width: "auto", padding: "6px 8px", fontSize: 12}}/>
                        </label>
                        <label style={{fontSize: 12, color: C.textMuted, display: "flex", alignItems: "center", gap: 4}}>
                            Au
                            <input type="date" value={userDateTo} onChange={e => setUserDateTo(e.target.value)}
                                   style={{...styles.input, marginBottom: 0, width: "auto", padding: "6px 8px", fontSize: 12}}/>
                        </label>
                    </div>

                    {userActionMsg && (
                        <p style={{fontSize: 13, marginBottom: 12, color: userActionMsg.startsWith("✅") ? C.primary : C.danger}}>
                            {userActionMsg}
                        </p>
                    )}

                    <div style={{backgroundColor: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, overflowX: "auto"}}>
                        {filteredUsers.length === 0 && (
                            <p style={{color: C.textMuted, padding: 16, fontStyle: "italic", fontSize: 13}}>Aucun utilisateur.</p>
                        )}
                        {filteredUsers.map(u => {
                            const isSelf = u.id === user.id;
                            return (
                                <div key={u.id} style={{
                                    display: "grid",
                                    gridTemplateColumns: "1.1fr 1.4fr 1.2fr 0.9fr 1.6fr 2fr",
                                    alignItems: "center", gap: 8,
                                    padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
                                }}>
                                    <span style={{fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={u.username}>
                                        {u.username}{isSelf && " (vous)"}
                                    </span>
                                    <span style={{fontSize: 12, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={u.email}>
                                        {u.email}
                                    </span>
                                    <span style={{fontSize: 12, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={u.groups || ""}>
                                        {u.groups || "—"}
                                    </span>
                                    <span style={{fontSize: 12, color: C.textMuted}}>
                                        {u.created_at ? u.created_at.slice(0, 10) : "?"}
                                    </span>
                                    <span style={{display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap"}}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
                                            backgroundColor: u.blocked_at ? "#FDECEA" : "#E8F4F1",
                                            color: u.blocked_at ? C.danger : C.primary,
                                        }}>{u.blocked_at ? "Suspendu" : "Actif"}</span>
                                        {!u.email_verified && (
                                            <span style={{fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, backgroundColor: "#FEF3C7", color: "#92400E", whiteSpace: "nowrap"}}>
                                                Email non vérifié
                                            </span>
                                        )}
                                    </span>
                                    <span style={{display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap", justifyContent: "flex-end"}}>
                                        {isSelf && <span style={{fontSize: 12, color: C.textMuted, fontStyle: "italic"}}>— vous —</span>}
                                        {!isSelf && (
                                            <>
                                                {!u.email_verified && (
                                                    <button onClick={() => handleVerifyUser(u)} style={{...styles.button, fontSize: 12, padding: "4px 10px"}}>
                                                        Vérifier manuellement
                                                    </button>
                                                )}
                                                <select
                                                    value={u.role}
                                                    onChange={e => handleChangeRole(u, e.target.value)}
                                                    style={{...styles.input, marginBottom: 0, width: "auto", padding: "4px 8px", fontSize: 12}}
                                                >
                                                    <option value="agent">Agent</option>
                                                    <option value="agent_sappm">Agent SAPPM</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                                <button onClick={() => handleToggleBlock(u)} style={{...styles.button, fontSize: 12, padding: "4px 10px"}}>
                                                    {u.blocked_at ? "Débloquer" : "Suspendu"}
                                                </button>
                                                <button onClick={() => handleDeleteUser(u)} style={{...styles.dangerButton, fontSize: 12, padding: "4px 10px"}}>
                                                    Supprimer
                                                </button>
                                                <button onClick={() => handlePurgeUser(u)} style={{...styles.dangerButton, fontSize: 12, padding: "4px 10px"}}>
                                                    Supprimer définitivement
                                                </button>
                                            </>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {showCreateUserModal && (
                        <Modal title="Créer un utilisateur" onClose={() => setShowCreateUserModal(false)}>
                            <input value={createUserEmail} onChange={e => setCreateUserEmail(e.target.value)}
                                   placeholder="Email professionnel" style={styles.input}/>
                            <input type="password" value={createUserPassword} onChange={e => setCreateUserPassword(e.target.value)}
                                   placeholder="Mot de passe initial" style={styles.input}/>
                            <select value={createUserRole} onChange={e => setCreateUserRole(e.target.value)} style={styles.input}>
                                <option value="agent">Agent</option>
                                <option value="agent_sappm">Agent SAPPM</option>
                                <option value="admin">Admin</option>
                            </select>
                            <p style={{fontSize: 12, color: C.textMuted}}>
                                Le pseudo est généré automatiquement. L'agent devra changer ce mot de passe à sa
                                première connexion.
                            </p>
                            <button onClick={handleCreateUser} style={styles.primaryButton}>Créer</button>
                            {createUserMsg && (
                                <p style={{marginTop: 8, fontSize: 13, color: createUserMsg.startsWith("✅") ? C.primary : C.danger}}>
                                    {createUserMsg}
                                </p>
                            )}
                        </Modal>
                    )}
                </>
            )}

            {adminTab === "ia-quiz" && isFullAdmin && (
                <AdminIaQuizPanel user={user}/>
            )}

            {adminTab === "campagnes" && isFullAdmin && (
                <AdminCampaigns/>
            )}

            {adminTab === "landing" && isFullAdmin && (
                <AdminLanding user={user}/>
            )}
        </div>
    );
}
